import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StoriesHero from './components/StoriesHero';
import StoriesFilters from './components/StoriesFilters';
import StoryCard from './components/StoryCard';
import { supabase } from '../../lib/supabase';
import SEOHead from '../../components/feature/SEOHead';

const STORIES_PER_PAGE = 9;

interface Story {
  id: string;
  title: string;
  genre: string;
  content: string;
  cover_image: string | null;
  like_count: number;
  read_count: number;
  published_date: string;
  profiles: {
    display_name: string;
  };
}

const getReadTimeMinutes = (content: string): number => {
  const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  return Math.ceil(words / 200);
};

const StoriesPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'All'
  );
  const [selectedSort, setSelectedSort] = useState('top');
  const [selectedReadTime, setSelectedReadTime] = useState('all');
  const [page, setPage] = useState(1);
  const [scrolled, setScrolled] = useState(false);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('stories')
          .select(`
            id,
            title,
            genre,
            content,
            cover_image,
            like_count,
            read_count,
            published_date,
            profiles!stories_author_id_fkey (
              display_name
            )
          `)
          .eq('status', 'published')
          .order('published_date', { ascending: false });

        if (error) throw error;
        setStories(data || []);
      } catch (error) {
        console.error('Error fetching stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedCategory, selectedSort, selectedReadTime]);

  const filtered = useMemo(() => {
    let result = [...stories];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.profiles.display_name.toLowerCase().includes(q) ||
          s.content.toLowerCase().includes(q) ||
          s.genre.toLowerCase().includes(q)
      );
    }

    // Category
    if (selectedCategory !== 'All') {
      result = result.filter((s) => s.genre === selectedCategory);
    }

    // Read time
    if (selectedReadTime === 'short') {
      result = result.filter((s) => getReadTimeMinutes(s.content) < 5);
    } else if (selectedReadTime === 'medium') {
      result = result.filter(
        (s) =>
          getReadTimeMinutes(s.content) >= 5 &&
          getReadTimeMinutes(s.content) <= 15
      );
    } else if (selectedReadTime === 'long') {
      result = result.filter((s) => getReadTimeMinutes(s.content) > 15);
    }

    // Sort
    if (selectedSort === 'newest') {
      result.sort(
        (a, b) =>
          new Date(b.published_date).getTime() -
          new Date(a.published_date).getTime()
      );
    } else if (selectedSort === 'oldest') {
      result.sort(
        (a, b) =>
          new Date(a.published_date).getTime() -
          new Date(b.published_date).getTime()
      );
    } else if (selectedSort === 'top') {
      result.sort((a, b) => b.like_count - a.like_count);
    } else if (selectedSort === 'most_read') {
      result.sort((a, b) => b.read_count - a.read_count);
    }

    return result;
  }, [stories, searchQuery, selectedCategory, selectedSort, selectedReadTime]);

  const totalPages = Math.ceil(filtered.length / STORIES_PER_PAGE);
  const paginated = filtered.slice(0, page * STORIES_PER_PAGE);
  const hasMore = page < totalPages;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Horror Stories Archive | Browse Dark Fiction & Creepypasta – Sinister Files"
        description="Explore hundreds of horror stories spanning psychological horror, supernatural tales, cosmic dread and creepypasta. Filter by genre, read time, and popularity on Sinister Files."
        canonical="https://sinisterfiles.com/stories"
      />
      <Navbar scrolled={scrolled} />

      <StoriesHero searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <StoriesFilters
        selectedCategory={selectedCategory}
        selectedSort={selectedSort}
        selectedReadTime={selectedReadTime}
        onCategoryChange={setSelectedCategory}
        onSortChange={setSelectedSort}
        onReadTimeChange={setSelectedReadTime}
        totalCount={filtered.length}
      />

      <main className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <div key={i} className="bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden animate-pulse">
                <div className="h-56 bg-gray-800"></div>
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-800 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-800 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-800 rounded"></div>
                  <div className="h-4 bg-gray-800 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-red-900/10 rounded-full mb-6">
              <i className="ri-ghost-line text-4xl text-red-800" />
            </div>
            <h3 className="text-2xl font-bold text-white font-['Cinzel'] mb-3">
              No Stories Found
            </h3>
            <p className="text-gray-500 font-['Crimson_Text'] max-w-md">
              The darkness holds no tales matching your search. Try different
              keywords or clear your filters.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
                setSelectedReadTime('all');
              }}
              className="mt-6 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-['Crimson_Text'] transition-colors cursor-pointer whitespace-nowrap"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((story, index) => (
                <StoryCard key={story.id} story={story} index={index} />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="px-10 py-3 bg-[#0f0f0f] border border-red-900/30 hover:border-red-600/60 text-gray-300 hover:text-white rounded-lg text-sm font-['Crimson_Text'] transition-all duration-300 cursor-pointer whitespace-nowrap group"
                >
                  <i className="ri-arrow-down-line mr-2 group-hover:animate-bounce inline-block" />
                  Load More Stories
                  <span className="ml-2 text-gray-600 text-xs">
                    ({filtered.length - paginated.length} remaining)
                  </span>
                </button>
              </div>
            )}

            {!hasMore && filtered.length > STORIES_PER_PAGE && (
              <p className="text-center text-gray-600 text-sm font-['Crimson_Text'] mt-12">
                You have reached the end of the archive.
              </p>
            )}
          </>
        )}
      </main>

      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default StoriesPage;